import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import {
  DataGrid,
  DataGridProps,
  GridCellParams,
  GridRenderEditCellParams,
  GridEditInputCell,
} from '@mui/x-data-grid';
import React from 'react';

import type { RowWithErrors } from './DataGrid';

const colorPrimary = '#216869';
const colorPrimaryExtraLight = '#e2f6f6';
const colorWarning = '#e79c0c';

const gridSx: DataGridProps['sx'] = {
  border: 'none',
  fontFamily: 'Lato',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '28px',
  color: '#333333',
  '.MuiDataGrid-main, .MuiDataGrid-columnHeaders': {
    borderBottom: `1px solid ${colorPrimary}`,
  },
  '.MuiDataGrid-virtualScroller': {
    borderLeft: `1px solid ${colorPrimary}`,
    borderRight: `1px solid ${colorPrimary}`,
  },
  '.MuiDataGrid-columnHeadersInner': {
    // Since we added padding on the left/right, we also need this
    // padding to have the cell borders line up with the rest of the grid
    padding: `0 1px`,
    alignSelf: 'stretch',
  },
  // set on both the title and containerContent so these styles apply to our custom headers as well
  '.MuiDataGrid-columnHeaderTitle, .MuiDataGrid-columnHeaderTitleContainerContent': {
    fontSize: '12px',
    lineHeight: '14px',
    fontWeight: 700,
  },
  '.MuiDataGrid-columnHeader': {
    alignSelf: 'stretch',
    '&.MuiDataGrid-withBorder': {
      borderRight: `1px solid ${colorPrimary}`,
    },
  },
  '.MuiDataGrid-cell': {
    backgroundColor: 'rgba(226, 246, 246, 0.2)',
    '&.MuiDataGrid-withBorder': {
      borderRight: '1px solid rgba(33, 104, 105, 0.2)',
    },
    '&:focus': {
      outline: `1px solid ${colorPrimary}`,
    },
    // Single Select cells do not contain focus while editing while normal text cells do.
    // The simple editing class is not string enough to override text cells, so we need
    // both of these selectors.
    '&.MuiDataGrid-cell--editing, &.MuiDataGrid-cell--editing:focus-within': {
      outline: 'none',
      backgroundColor: colorPrimaryExtraLight,
    },
  },
  '.MuiDataGrid-row': {
    '&.Mui-selected': {
      backgroundColor: colorPrimaryExtraLight,
    },
    '&.MuiDataGrid-row--editing': {
      outline: `2px solid ${colorPrimary}`,
      outlineOffset: -2,
    },
  },
};

export const StyledGrid = styled(DataGrid)(gridSx) as typeof DataGrid;

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: colorWarning,
    color: 'black',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: colorWarning,
  },
}));

export interface StringEditWithValidationProps
  extends GridRenderEditCellParams<string, RowWithErrors> {
  errorMsg?: string;
}

export function renderStringEditWithValidation(
  props: StringEditWithValidationProps,
) {
  const { errorMsg, ...gridProps } = props;
  const explicitErrorMsg = props.row.errors[props.field];

  const errorMsgToUse = errorMsg || explicitErrorMsg || '';
  const hasError = !!errorMsgToUse;

  // StyledTooltip requires its child to accept a ref, and GridEditInputCell is a function component
  return (
    <StyledTooltip
      arrow
      open={hasError}
      title={errorMsgToUse}
      componentsProps={{
        popper: {
          // disable portal to prevent the tooltip from leaving the grid as we scroll
          // horizontally or vertically. Poppers automatic clip parent detection does
          // not appear to work here and this is a simple workaround
          disablePortal: true,
        },
      }}
    >
      <div>
        <GridEditInputCell {...gridProps} />
      </div>
    </StyledTooltip>
  );
}

export function CellWithValidationTooltip(
  props: GridCellParams<any, RowWithErrors>,
) {
  const fieldError = props.row.errors[props.field];
  const hasError = !!fieldError && fieldError.length > 0;
  return (
    <div style={{ flex: '1' }}>
      <StyledTooltip
        arrow
        open={hasError}
        disableFocusListener
        title={fieldError || ''}
        componentsProps={{
          popper: {
            // disable portal to prevent the tooltip from leaving the grid as we scroll
            // horizontally or vertically. Poppers automatic clip parent detection does
            // not appear to work with the grids virtual scrolling.
            disablePortal: true,
          },
        }}
      >
        <div style={{ width: '100%' }}>{props.formattedValue}</div>
      </StyledTooltip>
    </div>
  );
}
